import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * refresh-parent-portal-ui 0.1: a status pill. Kinds carry the proposal's
 * colours and a default icon (overridable with the `icon` input).
 */
@Component({
  selector: 'ui-status-pill',
  standalone: true,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="pill">
      @if (iconOrDefault()) {
        <mat-icon>{{ iconOrDefault() }}</mat-icon>
      }
      {{ label() }}
    </span>
  `,
  styles: [
    `
      .pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px 4px 6px;
        border-radius: 999px;
        font-weight: 800;
        font-size: 13px;
        white-space: nowrap;
      }
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        line-height: 18px;
        font-family: 'Material Symbols Rounded', 'Material Icons';
      }
      :host([data-kind='success']) .pill {
        background: var(--gp-success-bg);
        color: var(--gp-success);
      }
      :host([data-kind='pending']) .pill {
        background: var(--gp-pending-bg);
        color: var(--gp-pending);
      }
      :host([data-kind='neutral']) .pill {
        background: var(--gp-neutral-bg);
        color: var(--gp-neutral);
      }
      :host([data-kind='info']) .pill {
        background: var(--gp-info-bg);
        color: var(--gp-info);
      }
      :host([data-kind='danger']) .pill {
        background: var(--gp-danger-bg);
        color: var(--gp-danger);
      }
    `,
  ],
  host: {
    '[attr.data-kind]': 'kind()',
  },
})
export class UiStatusPillComponent {
  readonly kind = input<'success' | 'pending' | 'neutral' | 'info' | 'danger'>('neutral');
  readonly label = input.required<string>();
  readonly icon = input('');

  static readonly DEFAULT_ICONS: Record<string, string> = {
    success: 'check_circle',
    pending: 'radio_button_unchecked',
    neutral: 'more_horiz',
    info: 'draw',
    danger: 'error',
  };

  readonly iconOrDefault = computed(() => {
    const i = this.icon();
    if (i) return i;
    return UiStatusPillComponent.DEFAULT_ICONS[this.kind()] ?? '';
  });
}